// Ported from relay-protocol packages/relay-widget/src/useDepositAddress.ts
// (v0.1.0). Mechanical substitution only: the seven wagmi hooks the original
// consumed (useAccount / useBalance / usePublicClient / useSendTransaction /
// useSwitchChain / useWaitForTransactionReceipt / useWriteContract) are
// replaced by the injected `opts.wallet` functions (RelayWalletProps), and
// viem's numeric/address helpers by ../lib/units -- dapp-ui may depend on
// neither wagmi nor viem. The orders-list effect (original lines 642-737) now
// lives in ./useRelayOrders, so the `orders`/`ordersLoading`/`ordersError`
// fields are gone from this hook's result. Everything else -- the catalog
// load, default (chain,token) select, controlled recipient default, debounced
// amount -> quote, and the EVM one-click deposit -- is the original logic.
//
// Headless orchestration hook for the ADDRESS-FIRST deposit flow (Task 4).
// Replaces useRelayOrder now that POST /v1/orders is gone: there is no
// order-creation step any more -- a deposit address is a stable, reusable
// per-(user, origin) resource fetched via GET /v1/deposit-address, shown
// immediately with no amount required. Amount is entirely optional and only
// drives a POST /v1/quote preview. The resulting orders (one per deposit
// the sweeper detects at that address) are surfaced by polling
// GET /v1/orders?user=.
//
// Ports the catalog load / default (chain,token) select / controlled
// recipient default / debounced amount -> quote / EVM one-click deposit
// machinery from the old useRelayOrder (frontend/src/App.tsx +
// DepositStep.tsx lineage), re-targeted at the fetched deposit address
// instead of a per-order one, with the create-order plumbing removed
// entirely (no dead createOrder path).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createRelayClient } from '../lib/client'
import type { RelayClient } from '../lib/client'
import type { Catalog, DepositAddressResult, OriginOption, QuoteResult } from '../lib/types'
import { formatUnits, isAddress, parseUnits, zeroAddress } from '../lib/units'
import type { Hex, RelayBalance, RelayWalletProps } from '../types'
import { depositAddressIsForwarder, verifyForwarderFromAnyFactory } from '../lib/verifyForwarder'
import { useDebouncedValue } from './useDebouncedValue'

const AMOUNT_DEBOUNCE_MS = 500
/** Standing forwarders always live on BSC, no matter which origin chain the
 * deposit comes from -- so the one-click deposit's address verification reads
 * the factory on this chain specifically (see verifyForwarderFromAnyFactory). */
const FORWARDER_CHAIN_ID = 56

/**
 * The one origin route on which `depositAddress` IS the CREATE2 forwarder, and
 * must therefore equal the `forwarder` field of the same response (upstream
 * a8eaa22).
 *
 * The backend decides this as
 * `direct_same_token && originChainID == bscChainID && currency == bscUSDT`
 * (internal/standing/service.go): on that route it sets
 * `depositAddress = forwarder`, skipping Relay entirely. On every other route
 * `depositAddress` is Relay's own deposit address, which nothing on-chain
 * derives and no client can verify.
 *
 * Pinned here as a constant, NOT read from the API response or the catalog, for
 * the same reason `trustedFactories` is: the value decides whether a response
 * gets checked, so taking it from the response being checked would let an
 * attacker opt out of the check. Hardcoded alongside FORWARDER_CHAIN_ID, which
 * already pins this widget to BSC mainnet.
 *
 * Deployment caveat: a self-hosted backend running `direct_same_token = false`
 * (the sample configs' default) serves a Relay address for this route, so the
 * one-click deposit on it will fail closed with the verification error below.
 * Failing closed is the correct direction for a guard on where funds go; the
 * error message names this cause so an operator can act on it. The same applies
 * if a deployment changes its `bridge_dest` token away from the address below.
 */
const DIRECT_ROUTE = {
  chainId: FORWARDER_CHAIN_ID,
  /** BSC USDT -- the backend's `bridge_dest` / `bscUSDT`. */
  token: '0x55d398326f99059fF775485246999027B3197955',
} as const
/** Live quotes are only guaranteed fresh for 30s -- keep refetching on that
 * cadence while the (origin, amount) inputs are unchanged. */
/** Fallback quote refresh cadence, used only when a quote reports no `ttl` (an
 * older backend, or one with `relay.quote_ttl_seconds` unset). The server's own
 * ttl is preferred -- see quoteRefreshMs (upstream af35dd5). */
const QUOTE_REFRESH_FALLBACK_MS = 30_000
/** Floor on the refresh cadence, whatever a quote's `ttl` says. A misconfigured
 * (or hostile) one-second ttl would otherwise turn every open widget into a
 * once-per-second poller against a rate-limited upstream shared with
 * GET /v1/deposit-address -- the client must not be the thing that exhausts
 * that budget on the server's say-so. */
const QUOTE_REFRESH_MIN_MS = 5_000

/** How long to wait before refetching a quote: its own reported lifetime when
 * it has one, else the local fallback. Clamped at QUOTE_REFRESH_MIN_MS, and a
 * non-finite/non-positive ttl is ignored rather than trusted.
 *
 * Refetching ON the ttl (rather than at some fraction of it) is deliberate:
 * the ttl is how long the quote stays VALID, so refreshing when it lapses
 * replaces it exactly as it stops being true. */
function quoteRefreshMs(ttl: number | undefined): number {
  if (typeof ttl !== 'number' || !Number.isFinite(ttl) || ttl <= 0) {
    return QUOTE_REFRESH_FALLBACK_MS
  }
  return Math.max(QUOTE_REFRESH_MIN_MS, ttl * 1000)
}

/** Minimal ERC-20 ABI -- only `transfer`, mirrored from frontend/src/abi/erc20.ts.
 * (balanceOf isn't needed here -- the injected `wallet.getBalance` handles
 * ERC-20 reads itself.) */
const ERC20_TRANSFER_ABI = [
  {
    type: 'function',
    name: 'transfer',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'to', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    outputs: [{ name: '', type: 'bool' }],
  },
] as const

/** Parses a human decimal amount into smallest units, or null if empty/invalid/zero. */
function toSmallestUnits(human: string, decimals: number): bigint | null {
  const trimmed = human.trim()
  if (!trimmed) return null
  try {
    const value = parseUnits(trimmed, decimals)
    return value > 0n ? value : null
  } catch {
    return null
  }
}

export interface DefaultToken {
  chainId: number
  /** Token contract address, or the zero address for the chain's native asset. */
  address: string
}

export type DepositPhase = 'idle' | 'switching' | 'pending' | 'confirming' | 'confirmed' | 'error'

export interface DepositState {
  phase: DepositPhase
  /** True while wallet.switchChain/sendTransaction/writeContract is in flight. */
  isSubmitting: boolean
  isConfirming: boolean
  isConfirmed: boolean
  /** True once the connected wallet's active chain differs from the origin chain. */
  needsSwitch: boolean
  txHash?: string
  /** True when the confirmed receipt's status is "reverted". */
  reverted: boolean
  error?: string
}

export interface UseDepositAddressOptions {
  /** Pre-built client. Takes precedence over apiBaseUrl. */
  client?: RelayClient
  /** Used to build a client via createRelayClient when `client` isn't passed. */
  apiBaseUrl?: string
  /**
   * Injected wallet capabilities (dapp-ui depends on neither wagmi nor viem,
   * so the host supplies these). All optional: every one that's missing
   * degrades gracefully -- no `walletAddress` means "disconnected", no
   * `getBalance` means no balance read, and a missing
   * submit/read/waitForReceipt function disables the one-click deposit (fail
   * closed) rather than the whole widget.
   */
  wallet?: RelayWalletProps
  /** Restrict the catalog's origins to these chain ids, in addition to the server's own curation. */
  chains?: number[]
  /** Controlled recipient (the CROSS "user" identity the deposit address/orders resolve for);
   * when omitted, defaults to the connected wallet address. Required (directly or via a
   * connected wallet) to fetch a deposit address at all. */
  recipient?: string
  /** Controlled human-decimal amount; when omitted, an internal `amount` state is used. Entirely
   * optional -- it only drives the POST /v1/quote preview, never the deposit address fetch. */
  amount?: string
  /** Which (chainId, token address) to default-select once the catalog loads, instead of the first origin. */
  defaultToken?: DefaultToken
  /**
   * The selected delivery target's symbol (Task 9), from `Catalog.targets` --
   * fully controlled, mirroring `recipient`/`amount`: the caller (RelayDeposit,
   * via useTargetTokens) owns the selection state and passes the current
   * symbol in here. Sent as `getDepositAddress`'s `target` param only when
   * truthy; omitted (byte-identical to pre-Task-9 behavior) when
   * undefined/empty, in which case the backend resolves the default target.
   * Changing this re-fetches the deposit address for the new target.
   */
  target?: string
  /**
   * Overrides the pinned factory set the one-click deposit verifies the
   * response's forwarder against (security report L-005) -- only needed by a
   * self-hosted deployment running its own factories. Defaults to
   * DEFAULT_TRUSTED_FACTORIES.
   *
   * Deliberately NOT taken from the API: a list supplied by the same response
   * being checked would make the check circular. See verifyForwarder.ts.
   */
  trustedFactories?: readonly string[]
  onDepositAddress?: (result: DepositAddressResult) => void
  onDeposited?: (txHash: string) => void
  onError?: (error: Error) => void
}

export interface UseDepositAddressResult {
  /** Connected wallet address (from `opts.wallet.walletAddress`), or `undefined` when disconnected. */
  address: string | undefined
  /** Connection state -- `Boolean(wallet.walletAddress)`, exposed so callers don't re-derive it. */
  isConnected: boolean

  catalog?: Catalog
  catalogLoading: boolean
  catalogError?: Error

  origin?: OriginOption
  setOrigin: (origin: OriginOption) => void

  recipient: string
  setRecipient: (value: string) => void
  /** True when `recipient` is empty (untouched) or a valid address. */
  recipientValid: boolean

  amount: string
  setAmount: (value: string) => void

  /** Connected wallet's balance of the selected origin token, or undefined (loading/disconnected/svm). */
  balance?: RelayBalance & { formatted: string }
  balanceLoading: boolean
  /** True when the entered (non-debounced) amount exceeds `balance`. Always false for svm origins. */
  insufficientBalance: boolean

  /** True when the selected origin is Solana -- no balance read, no one-click deposit. */
  isSvmOrigin: boolean

  /** The reusable deposit address for (recipient, origin), fetched with no amount involved. */
  depositAddress?: string
  /** Full GET /v1/deposit-address response (forwarder/version metadata included). */
  depositAddressResult?: DepositAddressResult
  depositAddressLoading: boolean
  depositAddressError?: Error

  /** OPTIONAL amount -> quote preview. Undefined whenever `amount` is empty. */
  quote?: QuoteResult
  quoteLoading: boolean
  quoteError?: Error

  /** True when a one-click EVM deposit can be submitted right now (amount entered, EVM origin,
   * connected wallet, sufficient balance, deposit address resolved, wallet able to submit,
   * to verify the address on-chain, AND to wait for the resulting receipt -- see
   * `walletCanDeposit` for why the receipt wait is a hard requirement, not a nicety). */
  canDeposit: boolean
  /** EVM one-click deposit: switch chain (if needed) then send/writeContract the entered amount
   * to the fetched deposit address. No-op for svm origins or when no amount is entered -- callers
   * should fall back to the manual QR/address flow in both cases. */
  deposit: () => Promise<void>
  depositState: DepositState
}

export function useDepositAddress(opts: UseDepositAddressOptions = {}): UseDepositAddressResult {
  // Keep the latest opts (callbacks especially) in a ref so effects below can
  // read them without needing fresh-identity-triggered re-subscriptions.
  const optsRef = useRef(opts)
  optsRef.current = opts

  const client = useMemo(() => {
    if (opts.client) return opts.client
    if (!opts.apiBaseUrl) {
      throw new Error('useDepositAddress: either `client` or `apiBaseUrl` must be provided')
    }
    return createRelayClient({ baseUrl: opts.apiBaseUrl })
  }, [opts.client, opts.apiBaseUrl])

  const wallet = opts.wallet
  const address = wallet?.walletAddress
  const isConnected = Boolean(address)
  const connectedChainId = wallet?.walletChainId

  // ---- Catalog (once) -------------------------------------------------
  const [rawCatalog, setRawCatalog] = useState<Catalog>()
  const [catalogLoading, setCatalogLoading] = useState(true)
  const [catalogError, setCatalogError] = useState<Error>()

  useEffect(() => {
    let cancelled = false
    setCatalogLoading(true)
    client
      .getConfig()
      .then((result) => {
        if (cancelled) return
        setRawCatalog(result)
        setCatalogError(undefined)
      })
      .catch((err) => {
        if (cancelled) return
        setCatalogError(err instanceof Error ? err : new Error(String(err)))
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [client])

  const chains = opts.chains
  const filteredOrigins = useMemo(() => {
    if (!rawCatalog) return []
    if (!chains || chains.length === 0) return rawCatalog.origins
    const allow = new Set(chains)
    return rawCatalog.origins.filter((o) => allow.has(o.chainId))
  }, [rawCatalog, chains])

  // The catalog exposed to callers reflects the `chains` filter, so a host
  // building a picker from `catalog.origins` automatically respects it.
  const catalog = useMemo(
    () => (rawCatalog ? { ...rawCatalog, origins: filteredOrigins } : undefined),
    [rawCatalog, filteredOrigins],
  )

  // ---- Origin selection -------------------------------------------------
  const [origin, setOriginState] = useState<OriginOption>()

  // Default-select once the (filtered) catalog loads, honoring defaultToken.
  useEffect(() => {
    if (origin || filteredOrigins.length === 0) return
    const defaultToken = optsRef.current.defaultToken
    const match = defaultToken
      ? filteredOrigins.find(
          (o) =>
            o.chainId === defaultToken.chainId &&
            o.token.address.toLowerCase() === defaultToken.address.toLowerCase(),
        )
      : undefined
    setOriginState(match ?? filteredOrigins[0])
    // optsRef.current.defaultToken is read once per catalog load, not tracked
    // as a reactive dependency -- it only matters for the initial default.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredOrigins, origin])

  const [localDepositError, setLocalDepositError] = useState<string>()

  // The original relied on wagmi's write hooks resetting themselves on every
  // new write; the manual tx state below (see "EVM one-click deposit") has to
  // be cleared explicitly whenever an input that a submitted tx was built
  // from changes, or a stale hash/confirmed flag would describe the previous
  // (origin, amount, recipient) triple. Also called at the start of every
  // submit, which is exactly when wagmi's mutate() used to reset itself.
  //
  // `isConfirming` MUST be cleared here too: dropping `txHash` cancels the
  // in-flight receipt wait through the effect's cleanup, so that wait's
  // `.finally` no longer runs and nothing else would ever lower the flag --
  // leaving the phase stuck at 'confirming' and canDeposit false forever.
  const resetTxState = useCallback(() => {
    setTxHash(undefined)
    setIsConfirming(false)
    setIsConfirmed(false)
    setReverted(false)
    setTxSubmitError(undefined)
  }, [])

  const setOrigin = useCallback(
    (next: OriginOption) => {
      setOriginState(next)
      setLocalDepositError(undefined)
      resetTxState()
    },
    [resetTxState],
  )

  const isSvmOrigin = origin?.kind === 'svm'

  // ---- Amount (OPTIONAL -- drives only the quote preview) -------------------------------------------------
  const [amountState, setAmountState] = useState('')
  const amount = opts.amount !== undefined ? opts.amount : amountState

  const setAmount = useCallback(
    (value: string) => {
      setAmountState(value)
      setLocalDepositError(undefined)
      resetTxState()
    },
    [resetTxState],
  )

  const debouncedAmount = useDebouncedValue(amount, AMOUNT_DEBOUNCE_MS)
  const smallestAmount = origin ? toSmallestUnits(debouncedAmount, origin.token.decimals) : null
  // Immediate (non-debounced) parse, so insufficient-balance gating and the
  // one-click deposit's amount both react instantly rather than waiting on
  // the quote debounce.
  const enteredAmount = origin ? toSmallestUnits(amount, origin.token.decimals) : null

  // ---- Recipient -------------------------------------------------
  const [recipientState, setRecipientState] = useState('')
  const [recipientTouched, setRecipientTouched] = useState(false)
  const recipient = opts.recipient !== undefined ? opts.recipient : recipientState

  const setRecipient = useCallback(
    (value: string) => {
      setRecipientState(value)
      setRecipientTouched(true)
      setLocalDepositError(undefined)
      resetTxState()
    },
    [resetTxState],
  )

  // Default the recipient to the connected wallet, unless the caller controls
  // it (opts.recipient) or the user has already edited it locally.
  useEffect(() => {
    if (opts.recipient !== undefined) return
    if (address && !recipientTouched) {
      setRecipientState(address)
    }
  }, [address, recipientTouched, opts.recipient])

  const recipientValid = recipient.length === 0 || isAddress(recipient)
  // A valid, non-empty recipient -- the key that gates the deposit address
  // fetch (and, in the caller, the orders-list poll).
  const recipientKey = recipient.length > 0 && isAddress(recipient) ? recipient : undefined

  // ---- Balance -------------------------------------------------
  // Skipped for svm origins -- the token address is base58, not a 20-byte
  // EVM address, so the connected EVM wallet can't read it.
  const [balance, setBalance] = useState<(RelayBalance & { formatted: string }) | undefined>()
  const [balanceLoading, setBalanceLoading] = useState(false)
  // bump to refetch after a confirmed deposit
  const [balanceNonce, setBalanceNonce] = useState(0)
  // NOTE for hosts: the injected functions must be STABLE references
  // (useCallback/useMemo, or a module-level wallet adapter). This effect and
  // the receipt-wait effect below key off their identity, so a fresh inline
  // arrow on every render would re-fire them on every render.
  const getBalance = wallet?.getBalance

  useEffect(() => {
    if (!getBalance || !address || !origin || isSvmOrigin) {
      setBalance(undefined)
      setBalanceLoading(false)
      return
    }
    let cancelled = false
    setBalanceLoading(true)
    const token = origin.token.address.toLowerCase() === zeroAddress ? undefined : (origin.token.address as Hex)
    getBalance({ chainId: origin.chainId, address: address as Hex, token })
      .then((b) => {
        if (cancelled) return
        setBalance({ ...b, formatted: formatUnits(b.value, b.decimals) })
      })
      .catch(() => {
        if (!cancelled) setBalance(undefined)
      })
      .finally(() => {
        if (!cancelled) setBalanceLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [getBalance, address, origin, isSvmOrigin, balanceNonce])

  const balanceValue = balance?.value ?? null

  const insufficientBalance = Boolean(
    !isSvmOrigin && enteredAmount !== null && balanceValue !== null && enteredAmount > balanceValue,
  )

  // ---- Deposit address (no amount involved) -------------------------------------------------
  const [depositAddressResult, setDepositAddressResult] = useState<DepositAddressResult>()
  const [depositAddressLoading, setDepositAddressLoading] = useState(false)
  const [depositAddressError, setDepositAddressError] = useState<Error>()

  const originChainId = origin?.chainId
  const originCurrency = origin?.token.address
  // Fully controlled (Task 9) -- see UseDepositAddressOptions.target's doc.
  const targetSymbol = opts.target

  useEffect(() => {
    if (!recipientKey || !originChainId || !originCurrency) {
      setDepositAddressResult(undefined)
      setDepositAddressError(undefined)
      setDepositAddressLoading(false)
      return
    }

    let cancelled = false
    // Clear any previously-held address synchronously as the key inputs
    // change: a standing forwarder's deposit address is genuinely per-origin
    // (per recipient/chain/token), so the old origin's address is WRONG for
    // the new one -- not merely stale. Leaving it in place would let
    // DepositStep render the old address with the new origin's
    // chain/kind/currency (a QR/payment-URI mismatch), and could let a
    // one-click send target the wrong-chain address. Blank it until the
    // fresh fetch resolves.
    setDepositAddressResult(undefined)
    setDepositAddressError(undefined)
    setDepositAddressLoading(true)
    // `target` is only appended as a second arg when truthy -- an
    // empty/undefined target must call getDepositAddress with the EXACT same
    // single-argument shape as pre-Task-9, so byte-identical behavior (and
    // the existing toHaveBeenCalledWith assertions) hold when the host never
    // opts into target selection.
    const request = targetSymbol
      ? client.getDepositAddress({ user: recipientKey, originChainId, originCurrency }, targetSymbol)
      : client.getDepositAddress({ user: recipientKey, originChainId, originCurrency })
    request
      .then((result) => {
        if (cancelled) return
        setDepositAddressResult(result)
        setDepositAddressError(undefined)
        optsRef.current.onDepositAddress?.(result)
      })
      .catch((err) => {
        if (cancelled) return
        setDepositAddressResult(undefined)
        const e = err instanceof Error ? err : new Error(String(err))
        setDepositAddressError(e)
        optsRef.current.onError?.(e)
      })
      .finally(() => {
        if (!cancelled) setDepositAddressLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [client, recipientKey, originChainId, originCurrency, targetSymbol])

  // ---- Deposit-intent change -> drop the previous tx state -------------------------------------------------
  // The setOrigin/setAmount/setRecipient setters each reset the tx state, but
  // they only cover the UNCONTROLLED paths: `target`, `amount` and `recipient`
  // are all fully controllable props, and a host changing any of them never
  // goes through a setter here. Without this effect a confirmed banner (and the
  // canDeposit=false that comes with it) would sit under a deposit address
  // fetched for the NEW target/recipient -- describing a tx that was sent
  // somewhere else entirely.
  //
  // The key is everything a submitted tx was built from: the origin
  // (chain+token), the identity the address was minted for, the delivery
  // target, and the amount. `amount` belongs in it precisely because a
  // controlled amount change must reset too -- and including it costs the
  // uncontrolled path nothing, since setAmount already resets.
  //
  // Compared against the PREVIOUS key rather than tracked as a plain dep, so
  // the body runs only on a real change: the initial mount is skipped (the ref
  // starts at the first key), and a resubmit for the SAME key -- deposit()
  // setting isTxPending/txHash while nothing about the intent moved -- cannot
  // be clobbered mid-flight, which is what keeps the "reset at the start of a
  // resubmit, not when the new hash lands" behavior intact.
  const depositIntentKey = [
    originChainId ?? '',
    (originCurrency ?? '').toLowerCase(),
    (recipientKey ?? '').toLowerCase(),
    targetSymbol ?? '',
    amount.trim(),
  ].join('|')
  const lastDepositIntentKeyRef = useRef(depositIntentKey)

  useEffect(() => {
    if (lastDepositIntentKeyRef.current === depositIntentKey) return
    lastDepositIntentKeyRef.current = depositIntentKey
    setLocalDepositError(undefined)
    resetTxState()
  }, [depositIntentKey, resetTxState])

  // ---- Live quote (debounced, 30s refresh) -- OPTIONAL preview only -------------------------------------------------
  const [quote, setQuote] = useState<QuoteResult>()
  const [quoteLoading, setQuoteLoading] = useState(false)
  const [quoteError, setQuoteError] = useState<Error>()

  const smallestAmountKey = smallestAmount?.toString()

  useEffect(() => {
    if (!originChainId || !originCurrency || !smallestAmountKey) {
      setQuote(undefined)
      setQuoteError(undefined)
      setQuoteLoading(false)
      return
    }

    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    // Only the FIRST fetch for this (originChainId, originCurrency, amount)
    // key should flip `quoteLoading` -- background 30s refreshes for the same
    // key update `quote` in place once the new result arrives, but must not
    // toggle loading back on, or a quote-preview UI (which hides the existing
    // quote while loading) would flicker to a spinner every refresh even
    // though a perfectly good quote is still on screen.
    let isFirstFetch = true

    async function fetchQuote() {
      if (isFirstFetch) setQuoteLoading(true)
      // The cadence for the NEXT refetch comes from the quote we are about to
      // receive, so it is captured per-iteration rather than closed over once.
      // A failed fetch leaves it at the fallback: there is no fresh ttl to
      // believe, and retrying on the last good quote's lifetime would be
      // pacing off a quote that no longer exists.
      let nextDelay = QUOTE_REFRESH_FALLBACK_MS
      try {
        const result = await client.getQuote({
          originChainId: originChainId as number,
          originCurrency: originCurrency as string,
          amount: smallestAmountKey as string,
        })
        if (cancelled) return
        setQuote(result)
        setQuoteError(undefined)
        nextDelay = quoteRefreshMs(result.ttl)
      } catch (err) {
        if (cancelled) return
        setQuote(undefined)
        setQuoteError(err instanceof Error ? err : new Error(String(err)))
      } finally {
        if (!cancelled) setQuoteLoading(false)
        isFirstFetch = false
      }
      if (!cancelled) {
        timer = setTimeout(fetchQuote, nextDelay)
      }
    }

    fetchQuote()

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [client, originChainId, originCurrency, smallestAmountKey])

  // ---- EVM one-click deposit (ported from the old useRelayOrder), targeting
  // the fetched depositAddress instead of a per-order one. Only meaningful
  // when an amount has been entered -- an amount-less "send anything" deposit
  // has nothing for a wallet tx to carry, so callers must fall back to the
  // manual QR/address flow when `canDeposit` is false. -------------------------------------------------
  // The original expressed all of this declaratively through wagmi's
  // useSwitchChain/useSendTransaction/useWriteContract/useWaitForTransactionReceipt;
  // with injected async functions the same lifecycle is held in local state.
  const [isSwitching, setIsSwitching] = useState(false)
  const [isTxPending, setIsTxPending] = useState(false)
  const [txHash, setTxHash] = useState<Hex | undefined>()
  const [txSubmitError, setTxSubmitError] = useState<string | undefined>()

  const [isConfirming, setIsConfirming] = useState(false)
  const [isConfirmed, setIsConfirmed] = useState(false)
  const [reverted, setReverted] = useState(false)
  const waitForReceipt = wallet?.waitForReceipt

  // onDeposited fires only for a CONFIRMED, non-reverted deposit, once per tx
  // (upstream 83845fb). It used to fire as soon as a tx hash existed -- on
  // submission, before the receipt was known -- so a deposit that later
  // reverted still reported itself as completed. The callback's name is a
  // claim about settlement and integrators act on it (crediting a balance,
  // advancing a checkout step), so reporting an unconfirmed or reverted
  // transfer is a correctness bug, not a timing nicety. Callers that want the
  // earlier "submitted" signal have depositState.phase / depositState.txHash.
  const notifiedHashRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!txHash || !origin || !waitForReceipt) return
    let cancelled = false
    setIsConfirming(true)
    setIsConfirmed(false)
    setReverted(false)
    waitForReceipt({ chainId: origin.chainId, hash: txHash })
      .then((receipt) => {
        if (cancelled) return
        setIsConfirmed(true)
        setReverted(receipt.status === 'reverted')
        setBalanceNonce((n) => n + 1)
        if (receipt.status !== 'reverted' && txHash !== notifiedHashRef.current) {
          notifiedHashRef.current = txHash
          optsRef.current.onDeposited?.(txHash)
        }
      })
      .catch((err) => {
        if (!cancelled) setTxSubmitError(err instanceof Error ? err.message : String(err))
      })
      .finally(() => {
        if (!cancelled) setIsConfirming(false)
      })
    return () => {
      cancelled = true
    }
    // origin intentionally NOT tracked: the receipt belongs to the chain the tx
    // was sent on; origin changes mid-wait must not retarget the wait.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txHash, waitForReceipt])

  const needsSwitch = connectedChainId !== undefined && origin !== undefined && connectedChainId !== origin.chainId
  const isSubmitting = isSwitching || isTxPending
  const submitError = localDepositError ?? txSubmitError

  let depositPhase: DepositPhase = 'idle'
  if (submitError) depositPhase = 'error'
  else if (isConfirmed) depositPhase = 'confirmed'
  else if (isConfirming) depositPhase = 'confirming'
  else if (isSubmitting) depositPhase = isSwitching ? 'switching' : 'pending'

  const depositState: DepositState = {
    phase: depositPhase,
    isSubmitting,
    isConfirming,
    isConfirmed,
    needsSwitch,
    txHash,
    reverted: isConfirmed && reverted,
    error: submitError,
  }

  // Defense-in-depth against a stale/mismatched address (consistent with this
  // codebase's CREATE2/address-mismatch guarding style): only allow a
  // one-click deposit when the held address was actually fetched for the
  // currently-selected origin. Combined with the synchronous clear above this
  // is belt-and-suspenders, but it guarantees a send can never target an
  // address minted for a different chain/token.
  const addressMatchesOrigin = Boolean(
    origin &&
      depositAddressResult &&
      depositAddressResult.originChainId === origin.chainId &&
      depositAddressResult.originCurrency.toLowerCase() === origin.token.address.toLowerCase(),
  )

  // Every capability the one-click path needs: both submit paths (the branch
  // taken depends on the origin token), the on-chain read the forwarder
  // verification is built on, AND the receipt wait that owns the post-send
  // lifecycle. Missing `readContract` means the address cannot be verified, and
  // an unverifiable address is exactly the case where a send must not proceed
  // -- so the button is disabled rather than the check skipped (fail closed,
  // matching verifyForwarder.ts).
  //
  // `waitForReceipt` is part of the same quartet for a duplicate-send reason,
  // not a cosmetic one: it is the ONLY thing that raises `isConfirming` after a
  // successful send. Without it a submitted deposit leaves `isTxPending` false,
  // `isConfirming` false and `isConfirmed` false, so the phase drops straight
  // back to 'idle' and every canDeposit term is satisfied again -- an armed
  // button sitting beside a live tx hash, one click away from transferring the
  // funds twice. A host that cannot wait for receipts gets the QR/address flow
  // instead.
  const walletCanDeposit = Boolean(
    wallet?.sendTransaction && wallet?.writeContract && wallet?.readContract && wallet?.waitForReceipt,
  )

  const canDeposit = Boolean(
    isConnected &&
      walletCanDeposit &&
      origin &&
      !isSvmOrigin &&
      depositAddressResult &&
      addressMatchesOrigin &&
      enteredAmount &&
      enteredAmount > 0n &&
      !insufficientBalance &&
      !isSubmitting &&
      !isConfirming,
  )

  const deposit = useCallback(async () => {
    if (isSvmOrigin) return // display-only -- no one-click for svm origins.
    if (!origin || !depositAddressResult) return
    // No injected submit path -- nothing to send with, so this is a no-op and
    // the caller's manual QR/address flow is the only route.
    if (!wallet?.sendTransaction || !wallet?.writeContract) return

    setLocalDepositError(undefined)
    const amountWei = toSmallestUnits(amount, origin.token.decimals)
    if (!amountWei || amountWei <= 0n) {
      setLocalDepositError('enter an amount to use the one-click deposit')
      return
    }

    const isNative = origin.token.address.toLowerCase() === zeroAddress
    const targetAddress = depositAddressResult.depositAddress

    // Verify what the response claims before sending funds anywhere (security
    // report L-005). Scope, stated honestly:
    //
    //   * The `forwarder` in this same response IS verifiable, against a pinned
    //     factory and the chain -- so a response tampered in that field is
    //     caught here rather than after the money has moved.
    //   * On a bridged route `depositAddress` is RELAY's open deposit address.
    //     It is not a CREATE2 address of ours and nothing on-chain derives it,
    //     so no client-side computation can confirm it. That residual is
    //     documented in verifyForwarder.ts, not papered over.
    //   * On the direct BSC route the deposit address IS the forwarder, so the
    //     check below covers the exact address receiving the funds.
    //
    // Fails closed, and only for the one-click path: a user reading the address
    // off-screen is unaffected, so this cannot lock anyone out of depositing.
    //
    // The forwarder always lives on BSC, whatever origin chain the user is
    // depositing from, so the read is pinned to FORWARDER_CHAIN_ID rather than
    // the origin chain (see verifyForwarderFromAnyFactory).
    const verdict = await verifyForwarderFromAnyFactory(wallet?.readContract, FORWARDER_CHAIN_ID, {
      forwarder: depositAddressResult.forwarder,
      // `recipientKey`, not a response field -- this is what proves the
      // forwarder is derived for the identity the deposit is FOR. It must be
      // the same value the address was fetched with (see the deposit-address
      // effect above), which is the recipient, NOT necessarily the connected
      // wallet: with a controlled `recipient` prop (or a recipient typed into
      // SourceForm) the two differ, and verifying against the wallet would
      // mismatch on every such deposit. Mirrors useRecovery.ts's own
      // recipientKey-based verification.
      recipient: recipientKey,
      version: depositAddressResult.version,
      trusted: optsRef.current.trustedFactories,
    })
    if (!verdict.ok) {
      setLocalDepositError(`address verification failed: ${verdict.reason}`)
      return
    }

    // Bind the VERIFIED forwarder to the address actually receiving the funds
    // (upstream a8eaa22).
    //
    // The verification above proves `depositAddressResult.forwarder` is derived
    // from a pinned factory for the recipient. It says nothing about
    // `depositAddress`, which is the separate field this function transfers to.
    // On the direct route those two are the same address (the backend assigns
    // `depositAddress = forwarder`), so a response that keeps a valid
    // `forwarder` while substituting `depositAddress` would otherwise pass every
    // check and send the funds to the substituted address.
    //
    // Scoped to DIRECT_ROUTE deliberately. On a bridged route `depositAddress`
    // is Relay's address and legitimately differs from the forwarder --
    // requiring equality there would reject real deposits (prod carries a
    // bridged BSC/USDC origin on this very chain, so a chain-id-only test would
    // do exactly that).
    const isDirectRoute =
      origin.chainId === DIRECT_ROUTE.chainId &&
      origin.token.address.toLowerCase() === DIRECT_ROUTE.token.toLowerCase()
    if (isDirectRoute && !depositAddressIsForwarder(targetAddress, depositAddressResult.forwarder)) {
      setLocalDepositError(
        'address verification failed: deposit address does not match the verified forwarder ' +
          "(expected them to be identical on this route; check the backend's direct_same_token setting)",
      )
      return
    }

    try {
      if (needsSwitch) {
        if (!wallet?.switchChain) throw new Error('wallet cannot switch chains')
        setIsSwitching(true)
        try {
          await wallet.switchChain(origin.chainId)
        } finally {
          setIsSwitching(false)
        }
      }
      // Same reset wagmi's mutate() performed at the start of every write:
      // without it a resubmit would keep showing the PREVIOUS deposit's hash
      // and 'confirmed' phase for the whole signing window.
      resetTxState()
      setIsTxPending(true)
      try {
        const hash = isNative
          ? await wallet.sendTransaction({ chainId: origin.chainId, to: targetAddress as Hex, value: amountWei })
          : await wallet.writeContract({
              chainId: origin.chainId,
              address: origin.token.address as Hex,
              abi: ERC20_TRANSFER_ABI,
              functionName: 'transfer',
              args: [targetAddress as Hex, amountWei],
            })
        setTxHash(hash)
      } finally {
        setIsTxPending(false)
      }
    } catch (err) {
      setLocalDepositError((err as Error).message)
    }
  }, [isSvmOrigin, origin, depositAddressResult, amount, needsSwitch, wallet, recipientKey, resetTxState])

  return {
    address,
    isConnected,

    catalog,
    catalogLoading,
    catalogError,

    origin,
    setOrigin,

    recipient,
    setRecipient,
    recipientValid,

    amount,
    setAmount,

    balance,
    balanceLoading,
    insufficientBalance,

    isSvmOrigin,

    depositAddress: depositAddressResult?.depositAddress,
    depositAddressResult,
    depositAddressLoading,
    depositAddressError,

    quote,
    quoteLoading,
    quoteError,

    canDeposit,
    deposit,
    depositState,
  }
}
